import React, { useState, useEffect } from 'react';
import api from '../api';
import ImageManagementDashboard from './ImageManagementDashboard';
import LogisticsAnalytics from './LogisticsAnalytics';
import { formatCurrency as formatCurrencyUtil, formatIndian } from '../utils/formatNumber';

interface AnalyticsData {
  products: {
    total: number;
    active: number;
    draft: number;
    archived: number;
    withImages: number;
    withoutImages: number;
    avgPrice: number;
    totalValue: number;
  };
  inventory: {
    totalStock: number;
    totalValue: number;
    warehouses: number;
    lowStockItems: number;
    outOfStockItems: number;
    avgStockPerProduct: number;
  };
  orders: {
    total: number;
    pending: number;
    confirmed: number;
    shipped: number;
    delivered: number;
    cancelled: number;
    totalRevenue: number;
    avgOrderValue: number;
  };
  categories: {
    departments: number;
    categories: number;
    subcategories: number;
    topDepartments: Array<{ name: string; count: number }>;
  };
  recentActivity: {
    productsCreatedToday: number;
    productsUpdatedToday: number;
    ordersCreatedToday: number;
    ordersShippedToday: number;
    ordersDeliveredToday: number;
    stockMovementsToday: number;
    reservationsToday: number;
    shipmentsBookedToday: number;
  };
}

const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'inventory' | 'orders' | 'images' | 'logistics'>('overview');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month' | 'custom' | 'all'>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange, dateFrom, dateTo]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `/api/analytics/dashboard?range=${timeRange}`;
      if (timeRange === 'custom' && dateFrom && dateTo) {
        url += `&date_from=${dateFrom}&date_to=${dateTo}`;
      }
      const response = await api.get(url);
      setAnalytics(response.data);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!analytics) return;
    setExporting(true);
    
    try {
      let csvContent = '';
      const tab = activeTab === 'overview' ? 'all' : activeTab;
      
      if (tab === 'products' || tab === 'all') {
        csvContent += 'PRODUCTS ANALYTICS\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Products,${analytics.products.total}\n`;
        csvContent += `Active,${analytics.products.active}\n`;
        csvContent += `Draft,${analytics.products.draft}\n`;
        csvContent += `Archived,${analytics.products.archived}\n`;
        csvContent += `With Images,${analytics.products.withImages}\n`;
        csvContent += `Without Images,${analytics.products.withoutImages}\n`;
        csvContent += `Average Price,${analytics.products.avgPrice.toFixed(2)}\n`;
        csvContent += `Total Catalog Value,${analytics.products.totalValue.toFixed(2)}\n`;
        csvContent += '\n';
      }
      
      if (tab === 'inventory' || tab === 'all') {
        csvContent += 'INVENTORY ANALYTICS\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Stock Units,${analytics.inventory.totalStock}\n`;
        csvContent += `Total Inventory Value,${analytics.inventory.totalValue.toFixed(2)}\n`;
        csvContent += `Warehouses,${analytics.inventory.warehouses}\n`;
        csvContent += `Low Stock Items,${analytics.inventory.lowStockItems}\n`;
        csvContent += `Out of Stock Items,${analytics.inventory.outOfStockItems}\n`;
        csvContent += `Avg Stock Per Product,${analytics.inventory.avgStockPerProduct.toFixed(1)}\n`;
        csvContent += '\n';
      }
      
      if (tab === 'orders' || tab === 'all') {
        csvContent += 'ORDERS ANALYTICS\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Orders,${analytics.orders.total}\n`;
        csvContent += `Pending,${analytics.orders.pending}\n`;
        csvContent += `Confirmed,${analytics.orders.confirmed}\n`;
        csvContent += `Shipped,${analytics.orders.shipped}\n`;
        csvContent += `Delivered,${analytics.orders.delivered}\n`;
        csvContent += `Cancelled,${analytics.orders.cancelled}\n`;
        csvContent += `Total Revenue,${analytics.orders.totalRevenue.toFixed(2)}\n`;
        csvContent += `Average Order Value,${analytics.orders.avgOrderValue.toFixed(2)}\n`;
        csvContent += '\n';
      }
      
      if (tab === 'all') {
        csvContent += 'CATEGORIES\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Departments,${analytics.categories.departments}\n`;
        csvContent += `Categories,${analytics.categories.categories}\n`;
        csvContent += `Subcategories,${analytics.categories.subcategories}\n`;
        csvContent += '\nTop Departments\n';
        csvContent += 'Department,Product Count\n';
        analytics.categories.topDepartments.forEach(d => {
          csvContent += `${d.name},${d.count}\n`;
        });
        csvContent += '\n';
        
        csvContent += 'TODAY\'S ACTIVITY\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Products Created,${analytics.recentActivity.productsCreatedToday}\n`;
        csvContent += `Products Updated,${analytics.recentActivity.productsUpdatedToday}\n`;
        csvContent += `Orders Created,${analytics.recentActivity.ordersCreatedToday}\n`;
        csvContent += `Orders Shipped,${analytics.recentActivity.ordersShippedToday}\n`;
        csvContent += `Orders Delivered,${analytics.recentActivity.ordersDeliveredToday}\n`;
        csvContent += `Stock Movements,${analytics.recentActivity.stockMovementsToday}\n`;
        csvContent += `Reservations,${analytics.recentActivity.reservationsToday}\n`;
        csvContent += `Shipments Booked,${analytics.recentActivity.shipmentsBookedToday}\n`;
      }
      
      // Add metadata
      const rangeLabel = timeRange === 'custom' ? `${dateFrom} to ${dateTo}` : timeRange;
      csvContent = `Report Generated: ${new Date().toLocaleString()}\nDate Range: ${rangeLabel}\nTab: ${activeTab}\n\n` + csvContent;
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${activeTab}_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return formatCurrencyUtil(amount, 2);
  };

  const formatNumber = (num: number) => {
    return formatIndian(num);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#6c757d' }}>Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ 
          padding: '1rem', 
          background: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header with Tabs */}
      <div style={{ 
        padding: '16px 20px', 
        background: '#f8f9fa', 
        borderBottom: '2px solid #dee2e6',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
            📊 Analytics Dashboard
          </h2>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Date range filter */}
            <select
              value={timeRange}
              onChange={(e) => {
                const val = e.target.value as any;
                setTimeRange(val);
                if (val !== 'custom') {
                  setDateFrom('');
                  setDateTo(new Date().toISOString().split('T')[0]);
                }
              }}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
              <option value="all">All Time</option>
            </select>

            {timeRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    fontSize: '13px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    background: 'white'
                  }}
                />
                <span style={{ fontSize: '13px', color: '#6c757d' }}>to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    padding: '5px 10px',
                    fontSize: '13px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    background: 'white'
                  }}
                />
              </>
            )}

            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={exporting || !analytics}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: '600',
                background: exporting ? '#94a3b8' : '#0d9488',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: exporting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {exporting ? '⏳ Exporting...' : '📥 Export CSV'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          {[
            { id: 'overview', label: 'Overview', icon: '📈' },
            { id: 'products', label: 'Products', icon: '📦' },
            { id: 'inventory', label: 'Inventory', icon: '📊' },
            { id: 'orders', label: 'Orders', icon: '🛒' },
            { id: 'logistics', label: 'Logistics', icon: '🚚' },
            { id: 'images', label: 'Images', icon: '🖼️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '14px 16px',
                background: activeTab === tab.id ? '#f0fdfa' : 'white',
                border: activeTab === tab.id ? '2px solid #0d9488' : '1px solid #ccfbf1',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>{tab.icon}</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: activeTab === tab.id ? '#0d9488' : '#5a6577', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{tab.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px', background: '#f8f9fa' }}>
        {activeTab === 'overview' && analytics && (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {/* Products Summary */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>📦</span>
                <h3 style={cardTitleStyle}>Products</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Total Products</span>
                  <span style={metricValueStyle}>{formatNumber(analytics.products.total)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Active</span>
                  <span style={{ ...metricValueStyle, color: '#28a745' }}>{formatNumber(analytics.products.active)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Draft</span>
                  <span style={{ ...metricValueStyle, color: '#ffc107' }}>{formatNumber(analytics.products.draft)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>With Images</span>
                  <span style={metricValueStyle}>{formatNumber(analytics.products.withImages)}</span>
                </div>
              </div>
            </div>

            {/* Inventory Summary */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>📊</span>
                <h3 style={cardTitleStyle}>Inventory</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Total Stock</span>
                  <span style={metricValueStyle}>{formatNumber(analytics.inventory.totalStock)} units</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Warehouses</span>
                  <span style={metricValueStyle}>{analytics.inventory.warehouses}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Low Stock Items</span>
                  <span style={{ ...metricValueStyle, color: '#ffc107' }}>{formatNumber(analytics.inventory.lowStockItems)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Out of Stock</span>
                  <span style={{ ...metricValueStyle, color: '#dc3545' }}>{formatNumber(analytics.inventory.outOfStockItems)}</span>
                </div>
              </div>
            </div>

            {/* Orders Summary */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>🛒</span>
                <h3 style={cardTitleStyle}>Orders</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Total Orders</span>
                  <span style={metricValueStyle}>{formatNumber(analytics.orders.total)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Pending</span>
                  <span style={{ ...metricValueStyle, color: '#ffc107' }}>{formatNumber(analytics.orders.pending)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Confirmed</span>
                  <span style={{ ...metricValueStyle, color: '#17a2b8' }}>{formatNumber(analytics.orders.confirmed)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Delivered</span>
                  <span style={{ ...metricValueStyle, color: '#28a745' }}>{formatNumber(analytics.orders.delivered)}</span>
                </div>
                <div style={{ ...metricRowStyle, borderTop: '1px solid #e9ecef', paddingTop: '8px', marginTop: '8px' }}>
                  <span style={metricLabelStyle}>Total Revenue</span>
                  <span style={{ ...metricValueStyle, color: '#007bff', fontWeight: '700' }}>
                    {formatCurrency(analytics.orders.totalRevenue)}
                  </span>
                </div>
              </div>
            </div>

            {/* Categories Summary */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>🏷️</span>
                <h3 style={cardTitleStyle}>Categories</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Departments</span>
                  <span style={metricValueStyle}>{analytics.categories.departments}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Categories</span>
                  <span style={metricValueStyle}>{analytics.categories.categories}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Subcategories</span>
                  <span style={metricValueStyle}>{analytics.categories.subcategories}</span>
                </div>
                {analytics.categories.topDepartments.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Top Departments
                    </div>
                    {analytics.categories.topDepartments.slice(0, 3).map((dept, idx) => (
                      <div key={idx} style={{ ...metricRowStyle, marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#495057' }}>{dept.name}</span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#007bff' }}>{dept.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>⚡</span>
                <h3 style={cardTitleStyle}>Today's Activity</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Products Created</span>
                  <span style={{ ...metricValueStyle, color: '#0d9488' }}>
                    +{formatNumber(analytics.recentActivity.productsCreatedToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Products Updated</span>
                  <span style={{ ...metricValueStyle, color: '#0d9488' }}>
                    {formatNumber(analytics.recentActivity.productsUpdatedToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Orders Created</span>
                  <span style={{ ...metricValueStyle, color: '#3b82f6' }}>
                    +{formatNumber(analytics.recentActivity.ordersCreatedToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Orders Shipped</span>
                  <span style={{ ...metricValueStyle, color: '#f59e0b' }}>
                    {formatNumber(analytics.recentActivity.ordersShippedToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Orders Delivered</span>
                  <span style={{ ...metricValueStyle, color: '#16a34a' }}>
                    {formatNumber(analytics.recentActivity.ordersDeliveredToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Stock Movements</span>
                  <span style={{ ...metricValueStyle, color: '#6366f1' }}>
                    {formatNumber(analytics.recentActivity.stockMovementsToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Reservations</span>
                  <span style={{ ...metricValueStyle, color: '#f59e0b' }}>
                    {formatNumber(analytics.recentActivity.reservationsToday)}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Shipments Booked</span>
                  <span style={{ ...metricValueStyle, color: '#8b5cf6' }}>
                    {formatNumber(analytics.recentActivity.shipmentsBookedToday)}
                  </span>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: '24px' }}>📈</span>
                <h3 style={cardTitleStyle}>Key Metrics</h3>
              </div>
              <div style={cardBodyStyle}>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Avg Product Price</span>
                  <span style={metricValueStyle}>{formatCurrency(analytics.products.avgPrice)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Avg Order Value</span>
                  <span style={metricValueStyle}>{formatCurrency(analytics.orders.avgOrderValue)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Avg Stock/Product</span>
                  <span style={metricValueStyle}>{analytics.inventory.avgStockPerProduct.toFixed(1)} units</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>Image Coverage</span>
                  <span style={metricValueStyle}>
                    {analytics.products.total > 0 
                      ? ((analytics.products.withImages / analytics.products.total) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Report Generator */}
          <ActivityReport />
          </>
        )}

        {activeTab === 'images' && (
          <ImageManagementDashboard />
        )}

        {activeTab === 'logistics' && (
          <LogisticsAnalytics />
        )}

        {activeTab !== 'overview' && activeTab !== 'images' && analytics && (
          <div style={{ padding: '24px' }}>
            {activeTab === 'products' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Product Breakdown</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#475569' }}>Active</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: `${Math.min((analytics.products.active / Math.max(analytics.products.total, 1)) * 100, 100)}%`, minWidth: '20px', maxWidth: '120px', height: '6px', background: '#0d9488', borderRadius: '3px' }} />
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>{analytics.products.active}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#475569' }}>Draft</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: `${Math.min((analytics.products.draft / Math.max(analytics.products.total, 1)) * 100, 100)}%`, minWidth: '20px', maxWidth: '120px', height: '6px', background: '#f59e0b', borderRadius: '3px' }} />
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>{analytics.products.draft}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#475569' }}>Archived</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: `${Math.min((analytics.products.archived / Math.max(analytics.products.total, 1)) * 100, 100)}%`, minWidth: '20px', maxWidth: '120px', height: '6px', background: '#94a3b8', borderRadius: '3px' }} />
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>{analytics.products.archived}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Image Coverage</div>
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                    <div style={{ position: 'relative', width: '80px', height: '80px' }}>
                      <svg viewBox="0 0 36 36" style={{ width: '80px', height: '80px', transform: 'rotate(-90deg)' }}>
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e8ecf0" strokeWidth="3" />
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#0d9488" strokeWidth="3"
                          strokeDasharray={`${(analytics.products.withImages / Math.max(analytics.products.total, 1)) * 100} 100`} />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
                        {Math.round((analytics.products.withImages / Math.max(analytics.products.total, 1)) * 100)}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', color: '#475569', marginBottom: '4px' }}>With images: <strong>{analytics.products.withImages}</strong></div>
                      <div style={{ fontSize: '13px', color: '#475569' }}>Without images: <strong style={{ color: '#dc2626' }}>{analytics.products.withoutImages}</strong></div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Pricing</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Average Price</div>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(analytics.products.avgPrice)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Total Catalog Value</div>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: '#0d9488' }}>₹{analytics.products.totalValue.toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Top Departments</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {analytics.categories.topDepartments.map((dept, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < analytics.categories.topDepartments.length - 1 ? '1px solid #f0f3f5' : 'none' }}>
                        <span style={{ fontSize: '13px', color: '#475569' }}>{dept.name}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{dept.count} products</span>
                      </div>
                    ))}
                    {analytics.categories.topDepartments.length === 0 && (
                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>No data</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'inventory' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Stock Health</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px' }}>Total Stock Units</span>
                      <strong style={{ color: '#1e293b' }}>{analytics.inventory.totalStock.toLocaleString()}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px' }}>Inventory Value</span>
                      <strong style={{ color: '#0d9488' }}>₹{analytics.inventory.totalValue.toLocaleString()}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px' }}>Avg per Product</span>
                      <strong>{analytics.inventory.avgStockPerProduct.toFixed(0)} units</strong>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Alerts</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fef3c7', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#92400e' }}>Low Stock Items</span>
                      <strong style={{ color: '#f59e0b', fontSize: '16px' }}>{analytics.inventory.lowStockItems}</strong>
                    </div>
                    <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#991b1b' }}>Out of Stock</span>
                      <strong style={{ color: '#dc2626', fontSize: '16px' }}>{analytics.inventory.outOfStockItems}</strong>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Warehouses</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#0d9488' }}>{analytics.inventory.warehouses}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Active locations</div>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Order Pipeline</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      { label: 'Pending', value: analytics.orders.pending, color: '#f59e0b' },
                      { label: 'Confirmed', value: analytics.orders.confirmed, color: '#3b82f6' },
                      { label: 'Shipped', value: analytics.orders.shipped, color: '#8b5cf6' },
                      { label: 'Delivered', value: analytics.orders.delivered, color: '#16a34a' },
                      { label: 'Cancelled', value: analytics.orders.cancelled, color: '#dc2626' },
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '13px', color: '#475569', flex: 1 }}>{item.label}</span>
                        <strong style={{ fontSize: '14px', color: item.color }}>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Revenue</div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Total Revenue</div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#0d9488' }}>₹{analytics.orders.totalRevenue.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Average Order Value</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(analytics.orders.avgOrderValue)}</div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Fulfillment Rate</div>
                  <div style={{ position: 'relative', width: '100px', height: '100px', margin: '0 auto 12px' }}>
                    <svg viewBox="0 0 36 36" style={{ width: '100px', height: '100px', transform: 'rotate(-90deg)' }}>
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e8ecf0" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#16a34a" strokeWidth="3"
                        strokeDasharray={`${analytics.orders.total > 0 ? ((analytics.orders.delivered / analytics.orders.total) * 100) : 0} 100`} />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                      {analytics.orders.total > 0 ? Math.round((analytics.orders.delivered / analytics.orders.total) * 100) : 0}%
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '12px', color: '#64748b' }}>{analytics.orders.delivered} of {analytics.orders.total} delivered</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Styles
const cardStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  transition: 'transform 150ms, box-shadow 150ms'
};

const cardHeaderStyle: React.CSSProperties = {
  padding: '16px',
  background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

const cardTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '16px',
  fontWeight: '700',
  color: 'white'
};

const cardBodyStyle: React.CSSProperties = {
  padding: '16px'
};

const metricRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px'
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#6c757d',
  fontWeight: '500'
};

const metricValueStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#1e293b'
};

// Activity Report Component
const ActivityReport: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/analytics/activity-report', { params: { date_from: dateFrom, date_to: dateTo } });
      setReport(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to generate report');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ margin: '24px', padding: '24px', background: 'white', borderRadius: '10px', border: '1px solid #e8ecf0' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: '#1e293b' }}>📊 Activity Report</h3>
      
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <div>
          <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <button className="btn btn-primary" onClick={generateReport} disabled={loading} style={{ padding: '8px 16px' }}>
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {report && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div style={{ padding: '16px', background: '#f0fdfa', borderRadius: '8px', border: '1px solid #ccfbf1' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Products</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Created</span><strong style={{ color: '#0d9488' }}>{report.products.created}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}><span>Updated</span><strong>{report.products.updated}</strong></div>
          </div>
          <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Orders</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Created</span><strong style={{ color: '#3b82f6' }}>{report.orders.created}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Shipped</span><strong>{report.orders.shipped}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Delivered</span><strong style={{ color: '#16a34a' }}>{report.orders.delivered}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Cancelled</span><strong style={{ color: '#dc2626' }}>{report.orders.cancelled}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderTop: '1px solid #bfdbfe', paddingTop: '4px', marginTop: '4px' }}><span>Revenue</span><strong>₹{report.orders.revenue.toLocaleString()}</strong></div>
          </div>
          <div style={{ padding: '16px', background: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Inventory</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Stock Movements</span><strong>{report.inventory.stockMovements}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Reservations</span><strong>{report.inventory.reservations}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}><span>Releases</span><strong>{report.inventory.releases}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}><span>Goods Received</span><strong>{report.inventory.goodsReceived}</strong></div>
          </div>
          <div style={{ padding: '16px', background: '#fefce8', borderRadius: '8px', border: '1px solid #fef08a' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Logistics</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}><span>Shipments Booked</span><strong>{report.logistics.shipmentsBooked}</strong></div>
          </div>
          <div style={{ padding: '12px 16px', background: '#f8fafb', borderRadius: '8px', border: '1px solid #e8ecf0', gridColumn: '1 / -1' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Period: {report.period.from} to {report.period.to} ({report.period.days} days)</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
